// Hindernisse einer Platte — Ausschnitte, Bohrungen und Nuten/Fälze als
// teil-lokale, achsparallele Boxen (x = Länge, y = Stärke, z = Breite).
// Engine-frei. Die Boxen sind bewusst grob (Bohrung = Zylinder-Hülle,
// Nut/Falz = Quader, vgl. render/csg.js): für die Frage „ist an dieser Stelle
// noch volles Holz?“ reicht die Hülle, und sie liegt immer auf der sicheren
// Seite.
//
// Sicherheitsabstand (WEB_MM, 3 mm): Reststeg zwischen der Wand einer geplanten
// Bohrung und dem nächsten Abtrag. Der Bohrerradius steckt bereits in der Box,
// zusammen ergibt das die Werkstattregel „Bohrermitte mindestens Radius + 3 mm
// von jeder Kante eines anderen Abtrags“. 3 mm sind die übliche Untergrenze für
// Spanplatte/MDF — darunter bricht der Steg beim Bohren oder beim Eindrehen des
// Verbinders aus.
import { cutoutsOf, isCircle, cutoutAxis } from "./cutouts"
import { groovesOf, isFalz } from "./machinings"

export const WEB_MM = 3

// Stirnbohrung ohne brauchbare Tiefe: gleiche Annahme wie cutouts.js
const DEFAULT_END_GRAIN_DEPTH = 30

const num = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0)
const around = (center, half) => [ center - half, center + half ]
const box = (x, y, z) => ({ min: [ x[0], y[0], z[0] ], max: [ x[1], y[1], z[1] ] })

// Tiefenbereich einer von einer Fläche aus eingebrachten Bearbeitung:
// side "bottom" = von der Minus-Seite, sonst von der Plus-Seite.
function fromSide(size, depth, side) {
  return side === "bottom" ? [ -size / 2, -size / 2 + depth ] : [ size / 2 - depth, size / 2 ]
}

// Hüll-Box eines Ausschnitts/einer Bohrung im lokalen Teil-Frame.
export function cutoutBox(part, cutout) {
  const { length_mm: l, thickness_mm: s, width_mm: b } = part
  const x = num(cutout.x_mm)
  const y = num(cutout.y_mm)
  const z = num(cutout.z_mm)

  if (isCircle(cutout)) {
    const radius = num(cutout.diameter_mm) / 2
    const axis = cutoutAxis(cutout)
    const depth = num(cutout.depth_mm)

    if (axis === "x") {
      const along = fromSide(l, depth > 0 ? depth : DEFAULT_END_GRAIN_DEPTH, cutout.side)
      return box(along, around(y, radius), around(z, radius))
    }
    if (axis === "z") {
      const along = fromSide(b, depth > 0 ? depth : DEFAULT_END_GRAIN_DEPTH, cutout.side)
      return box(around(x, radius), around(y, radius), along)
    }
    // Flächenbohrung: ohne (oder mit zu großer) Tiefe durchgehend
    const along = depth > 0 && depth < s ? fromSide(s, depth, cutout.side) : [ -s / 2, s / 2 ]
    return box(around(x, radius), along, around(z, radius))
  }

  // rechteckiger Ausschnitt: immer durchgehend
  return box(around(x, num(cutout.length_mm) / 2), [ -s / 2, s / 2 ], around(z, num(cutout.width_mm) / 2))
}

// Hüll-Box einer Nut/eines Falzes (Konventionen wie render/csg.js).
export function grooveBox(part, groove) {
  const { length_mm: l, thickness_mm: s, width_mm: b } = part
  const width = num(groove.width_mm)
  const depth = fromSide(s, num(groove.depth_mm), groove.side)
  const fullX = [ -l / 2, l / 2 ]
  const fullZ = [ -b / 2, b / 2 ]

  if (isFalz(groove)) {
    switch (groove.edge) {
      case "l2": return box(fullX, depth, [ b / 2 - width, b / 2 ])
      case "b1": return box([ -l / 2, -l / 2 + width ], depth, fullZ)
      case "b2": return box([ l / 2 - width, l / 2 ], depth, fullZ)
      default: return box(fullX, depth, [ -b / 2, -b / 2 + width ]) // l1
    }
  }
  // Nut: offset_mm = Kante → Nutmitte, direction "z" ab linker (−x), sonst ab vorderer (−z)
  if (groove.direction === "z") return box(around(-l / 2 + num(groove.offset_mm), width / 2), depth, fullZ)
  return box(fullX, depth, around(-b / 2 + num(groove.offset_mm), width / 2))
}

// Alle Hindernis-Boxen eines Teils (Ausschnitte, Bohrungen, Nuten, Fälze).
export function obstacleBoxes(part, extraCutouts = []) {
  return [
    ...cutoutsOf(part).map((cutout) => cutoutBox(part, cutout)),
    ...extraCutouts.map((cutout) => cutoutBox(part, cutout)),
    ...groovesOf(part).map((groove) => grooveBox(part, groove))
  ]
}

// Zwei Boxen kollidieren, wenn sie auf keiner Achse mindestens clearanceMm
// auseinander liegen (Trennachsen-Test mit Sicherheitsabstand).
export function boxesClash(a, b, clearanceMm = 0) {
  return [ 0, 1, 2 ].every((i) =>
    Math.max(b.min[i] - a.max[i], a.min[i] - b.max[i]) < clearanceMm - 1e-9
  )
}

// Kollidiert die geplante Bohrung/der geplante Ausschnitt mit etwas, das auf
// dem Teil schon vorhanden ist? extra = zusätzliche, noch nicht gespeicherte
// Bearbeitungen desselben Laufs.
export function cutoutClashes(part, candidate, { clearanceMm = WEB_MM, extra = [] } = {}) {
  const candidateBox = cutoutBox(part, candidate)
  return obstacleBoxes(part, extra).some((other) => boxesClash(candidateBox, other, clearanceMm))
}
